import React from 'react';
import {
  FormGroup,
  Slider,
  StyleButtons,
  AdvancedColorInput,
  NavButtons,
} from '../common';

const applyPreviewTextStyles = (text, styles = []) => {
  if (!text) return null;
  let content = text;
  if (styles.includes('bold')) content = <strong>{content}</strong>;
  if (styles.includes('italic')) content = <em>{content}</em>;
  if (styles.includes('underline')) content = <u>{content}</u>;
  return content;
};

const WalletPreview = ({ settings, globalSettings }) => {
  const walletTextColor =
    settings.walletTextColor || settings.bodyTextColor || '#333333';
  const introText = settings.walletIntroText || 'Add to your digital wallet';
  const instructions = (settings.walletInstructionsText || 'Tap to add to your wallet')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  const appleWalletSrc = globalSettings?.imageUrls?.appleWallet || '';
  const googleWalletSrc = globalSettings?.imageUrls?.googleWallet || '';

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
      <div
        className="rounded-2xl p-5 shadow-sm"
        style={{
          backgroundColor: settings.walletBackgroundColor || '#f8f9fa',
          color: walletTextColor,
          textAlign: settings.walletTextAlign || 'left',
        }}
      >
        <p
          className="mb-4 text-sm"
          style={{ fontSize: `${settings.walletIntroFontSize || 14}pt` }}
        >
          {String(introText)
            .split('\n')
            .map((line, index) => (
              <React.Fragment key={`${line}-${index}`}>
                {applyPreviewTextStyles(line, settings.walletIntroStyles)}
                {index < String(introText).split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
        </p>
        <ol
          className={`mb-5 space-y-2 text-sm ${settings.walletTextAlign === 'left' ? 'pl-5' : 'list-inside pl-0'}`}
          style={{ fontSize: `${settings.walletInstructionsFontSize || 12}pt` }}
        >
          {instructions.map((line, index) => (
            <li key={`${line}-${index}`}>
              {applyPreviewTextStyles(line, settings.walletInstructionsStyles)}
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {appleWalletSrc ? (
            <img
              src={appleWalletSrc}
              alt="Apple Wallet preview"
              className="h-auto max-w-[150px] rounded-md"
            />
          ) : (
            <div className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white">
              Apple Wallet
            </div>
          )}
          {googleWalletSrc ? (
            <img
              src={googleWalletSrc}
              alt="Google Wallet preview"
              className="h-auto max-w-[171px] rounded-md"
            />
          ) : (
            <div className="rounded-lg bg-[#1a73e8] px-4 py-2 text-sm font-semibold text-white">
              Google Wallet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const WalletSection = ({ settings, onUpdate, onNavigate, globalSettings }) => {
  const prevSectionKey = () => {
    if (settings.includeCalendarButton) return 'calendarButton';
    if (settings.showCongressAppLinks) return 'congressAppLinks';
    return 'content';
  };

  return (
    <>
    <FormGroup title="Wallet Button Settings">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AdvancedColorInput
          label="Background Color"
          value={settings.walletBackgroundColor}
          onChange={v => onUpdate('walletBackgroundColor', v)}
        />
        <AdvancedColorInput
          label="Text Color"
          value={
            settings.walletTextColor ||
            settings.bodyTextColor ||
            '#333333'
          }
          onChange={v => onUpdate('walletTextColor', v)}
        />
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Text Alignment
          </label>
          <select
            value={settings.walletTextAlign || 'left'}
            onChange={e => onUpdate('walletTextAlign', e.target.value)}
            className="form-select block w-full"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
            <option value="justify">Justify</option>
          </select>
          <p className="mt-1.5 text-xs text-gray-500">
            Icons stay centered; only the text alignment changes.
          </p>
        </div>
        <div className="md:col-span-1 flex items-end">
          <p className="text-xs text-gray-500">
            Placement is controlled in Text Templates via <code>[[WALLET]]</code>.
          </p>
        </div>
      </div>
    </FormGroup>

    <FormGroup title="Introduction Text">
      <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
        <textarea
          rows="3"
          value={settings.walletIntroText || ''}
          onChange={e => onUpdate('walletIntroText', e.target.value)}
          className="form-textarea"
          placeholder="Enter introductory text..."
        />
        <p className="mt-1.5 text-xs text-gray-500">
          Use line breaks for new lines.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Slider
            label="Font Size"
            value={settings.walletIntroFontSize}
            onChange={v => onUpdate('walletIntroFontSize', v)}
            min={8}
            max={24}
            unit="pt"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Style
            </label>
            <StyleButtons
              styles={settings.walletIntroStyles}
              onToggle={v => onUpdate('walletIntroStyles', v)}
              targetId="walletIntro"
            />
          </div>
        </div>
      </div>
    </FormGroup>

    <FormGroup title="Instructions Text">
      <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
        <textarea
          rows="4"
          value={settings.walletInstructionsText || ''}
          onChange={e => onUpdate('walletInstructionsText', e.target.value)}
          className="form-textarea"
          placeholder="Enter instruction points..."
        />
        <p className="mt-1.5 text-xs text-gray-500">
          Each line will become a numbered list item.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Slider
            label="Font Size"
            value={settings.walletInstructionsFontSize}
            onChange={v => onUpdate('walletInstructionsFontSize', v)}
            min={8}
            max={24}
            unit="pt"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Style
            </label>
            <StyleButtons
              styles={settings.walletInstructionsStyles}
              onToggle={v => onUpdate('walletInstructionsStyles', v)}
              targetId="walletInstructions"
            />
          </div>
        </div>
      </div>
    </FormGroup>

    <FormGroup title="Wallet Image Assets">
      <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <h4 className="text-base font-semibold text-slate-900">
              Open the internal wallet image resizer
            </h4>
            <p className="mt-2 text-sm text-slate-500">
              Resize icons, strips, hero artwork, and wallet logos without leaving the configurator.
              The tool uses the same authenticated session and exports PNG assets directly in the browser.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onNavigate('walletImageResizer')}
            className="nav-button-primary w-full lg:w-auto"
          >
            Open Wallet Image Resizer
          </button>
        </div>
      </div>
    </FormGroup>

    <FormGroup title="Preview">
      <WalletPreview settings={settings} globalSettings={globalSettings} />
    </FormGroup>

    <NavButtons
      onPrev={() => onNavigate(prevSectionKey())}
      onNext={() => onNavigate('walletImageResizer')}
      nextLabel="Continue to Image Resizer"
    />
  </>
  );
};
export default WalletSection;
